// src/components/CustomFileUpload.js
import React, { useRef, useState, useImperativeHandle, forwardRef } from 'react';
import { FiUploadCloud, FiPaperclip, FiX } from 'react-icons/fi';

const CustomFileUpload = forwardRef(({ 
    onChange, 
    accept = "image/*,application/pdf", 
    multiple = false, 
    label = "Choose File",
    variant = "secondary", // 'primary' or 'secondary'
    showPreviewText = true,
    className = ""
}, ref) => {
    const internalInputRef = useRef(null);
    const [fileName, setFileName] = useState('');

    // Allow parent components to trigger the click (e.g., clicking a profile image)
    useImperativeHandle(ref, () => ({
        click: () => {
            internalInputRef.current.click();
        },
        clear: () => {
            setFileName('');
            if (internalInputRef.current) internalInputRef.current.value = '';
        }
    }));

    const handleClick = () => {
        internalInputRef.current.click();
    };

    const handleFileChange = (e) => {
        const files = e.target.files;
        if (files && files.length > 0) {
            if (files.length > 1) {
                setFileName(`${files.length} files selected`);
            } else {
                setFileName(files[0].name);
            }
            // Pass event back to parent
            if (onChange) onChange(e);
        } else {
            setFileName('');
        }
    };

    const handleClear = (e) => {
        e.stopPropagation();
        setFileName('');
        if (internalInputRef.current) internalInputRef.current.value = '';
        // Create synthetic event to clear parent state
        if (onChange) onChange({ target: { files: [] } });
    };

    // Determine button class based on variant prop and existing app styles
    const btnClass = variant === 'primary' 
        ? 'btn btn-primary btn-file-upload' 
        : 'btn btn-secondary btn-file-upload';

    return (
        <div className={`custom-file-upload-wrapper ${className}`}>
            <input
                type="file"
                ref={internalInputRef}
                onChange={handleFileChange}
                accept={accept}
                multiple={multiple}
                style={{ display: 'none' }}
            />
            
            <button type="button" className={btnClass} onClick={handleClick}>
                {multiple ? <FiPaperclip className="btn-icon" /> : <FiUploadCloud className="btn-icon" />}
                {label}
            </button>

            {showPreviewText && fileName && (
                <>
                    <span className="file-name-display" title={fileName}>
                        {fileName}
                    </span>
                    <button 
                        type="button" 
                        className="btn-icon-only btn-icon-danger" 
                        onClick={handleClear}
                        title="Clear selection"
                    >
                        <FiX size={16} />
                    </button>
                </>
            )}
        </div>
    );
});

export default CustomFileUpload;